import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Gamepad2, Joystick, Palette, LayoutTemplate,
  Trophy, CalendarDays, Rocket, ShoppingBag, Shield, Settings as SettingsIcon,
  Baby, Menu, LogOut, Sun, Moon, Zap, GraduationCap, Crown, Megaphone, AlertTriangle, X, Wand2, ScrollText, Users, Wallet, BarChart3, Box, Boxes, Coins, LifeBuoy, UserPlus, Compass, TrendingUp, Globe2, Heart, Activity, MessageSquare, GitFork, Swords, Film, Library, Drama, Shuffle, Images, Clapperboard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { LivePlayersBadge } from "@/components/LivePlayersBadge";
import { useMode } from "@/context/ModeContext";
import { useRoom } from "@/context/RoomContext";
import Logo from "@/components/Logo";
import DirectorAssistant from "@/components/DirectorAssistant";
import GlobalSearch from "@/components/GlobalSearch";
import InstallPrompt from "@/components/InstallPrompt";
import LobbyDock from "@/components/LobbyDock";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/create", label: "Create Game", icon: Sparkles, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/studio-pro", label: "Studio Pro", icon: Clapperboard, roles: ["teen", "creator", "educator", "admin"] },
  { to: "/app/games", label: "My Games", icon: Gamepad2, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/hub", label: "Community Hub", icon: Globe2, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/play", label: "Play Games", icon: Joystick, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/clips", label: "Community Clips", icon: Film, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/discover", label: "Discover", icon: Compass, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/challenges", label: "Remix Challenge", icon: Swords, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/favorites", label: "Favourites", icon: Heart, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/activity", label: "Activity Feed", icon: Activity, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/messages", label: "Messages", icon: MessageSquare, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/assets", label: "Asset Studio", icon: Palette, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/characters", label: "Character Studio", icon: Wand2, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/vn", label: "VN Studio", icon: Drama, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/mashup", label: "Mashup Studio", icon: Shuffle, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/mashup-gallery", label: "Mashup Wall", icon: Images, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/vn-gallery", label: "Scene Wall", icon: Compass, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/3d", label: "3D Studio", icon: Box, roles: ["teen", "student", "creator", "educator", "admin"] },
  { to: "/app/asset-hub", label: "Asset Hub", icon: Boxes, roles: ["teen", "student", "creator", "educator", "admin"] },
  { to: "/app/universe", label: "Asset Universe", icon: Library, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/templates", label: "Templates", icon: LayoutTemplate, roles: ["teen", "creator", "educator", "admin"] },
  { to: "/app/community-templates", label: "Community Templates", icon: GitFork, roles: ["kid", "teen", "student", "creator", "player", "educator", "admin"] },
  { to: "/app/achievements", label: "Achievements", icon: Trophy, roles: ["kid", "teen", "student", "creator", "player", "admin"] },
  { to: "/app/arena", label: "Events & Arena", icon: CalendarDays, roles: ["teen", "creator", "player", "educator", "admin"] },
  { to: "/app/multiplayer", label: "Multiplayer", icon: Users, roles: ["teen", "creator", "player", "educator", "admin"] },
  { to: "/app/friends", label: "Friends", icon: UserPlus, roles: ["kid", "teen", "student", "creator", "player", "educator", "admin"] },
  { to: "/app/guilds", label: "Guilds & Clans", icon: Shield, roles: ["teen", "student", "creator", "player", "educator", "admin"] },
  { to: "/app/creator", label: "Creator Hub", icon: Rocket, roles: ["creator", "admin"] },
  { to: "/app/creator-dashboard", label: "Creator Dashboard", icon: BarChart3, roles: ["creator", "educator", "admin"] },
  { to: "/app/school", label: "School Mode", icon: GraduationCap, roles: ["educator"] },
  { to: "/app/parent", label: "Parent Dashboard", icon: Baby, roles: ["parent"] },
  { to: "/app/marketplace", label: "Marketplace", icon: ShoppingBag, roles: ["teen", "creator", "player", "educator", "admin"] },
  { to: "/app/earnings", label: "Earnings", icon: Wallet, roles: ["creator", "educator", "admin"] },
  { to: "/app/growth", label: "Growth Producer", icon: TrendingUp, roles: ["creator", "educator", "admin"] },
  { to: "/app/credits", label: "Credits", icon: Coins, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/support", label: "Support", icon: LifeBuoy, roles: ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"] },
  { to: "/app/rights", label: "Ownership & Rights", icon: ScrollText, roles: ["teen", "creator", "educator", "admin"] },
  { to: "/app/admin", label: "Admin", icon: Shield, roles: ["admin"] },
  { to: "/app/superadmin", label: "Owner Console", icon: Crown, roles: ["superadmin"] },
];

// Thumb-reachable bottom tab bar (mobile only). First 4 role-appropriate items + a "More" drawer button.
const _ALL_ROLES = ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"];
const BOTTOM_NAV = [
  { to: "/app", label: "Home", icon: LayoutDashboard, roles: _ALL_ROLES },
  { to: "/app/create", label: "Create", icon: Sparkles, roles: ["kid", "teen", "student", "creator", "educator", "admin"] },
  { to: "/app/play", label: "Play", icon: Joystick, roles: _ALL_ROLES },
  { to: "/app/discover", label: "Discover", icon: Compass, roles: _ALL_ROLES },
  { to: "/app/marketplace", label: "Market", icon: ShoppingBag, roles: ["teen", "creator", "player", "educator", "admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { mode, toggle, isKid } = useMode();
  const { inviteCount } = useRoom();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [platform, setPlatform] = useState({ announcement: "", maintenance_mode: false });
  const [annDismissed, setAnnDismissed] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      setPlatform(r.data);
      setAnnDismissed(localStorage.getItem("gv_ann_dismissed") === (r.data.announcement || ""));
    }).catch(() => {});
  }, []);

  // presence heartbeat so friends see online status
  useEffect(() => {
    const ping = () => api.post("/social/presence").catch(() => {});
    ping();
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, []);

  // 🎬 "New trailer" badge — count games with a cinematic trailer vs what the user last saw on Discover.
  const [trailerNew, setTrailerNew] = useState(0);
  useEffect(() => {
    api.get("/arcade/trailers/count").then((r) => {
      const total = Number(r.data?.count || 0);
      if (location.pathname.startsWith("/app/discover")) {
        localStorage.setItem("gv_trailers_seen", String(total));
        setTrailerNew(0);
      } else {
        const seen = Number(localStorage.getItem("gv_trailers_seen") || "0");
        setTrailerNew(Math.max(0, total - seen));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const dismissAnn = () => {
    localStorage.setItem("gv_ann_dismissed", platform.announcement || "");
    setAnnDismissed(true);
  };

  if (!user) return null;
  const items = NAV.filter((n) => user.role === "superadmin" || n.roles.includes(user.role));
  const bottomItems = BOTTOM_NAV.filter((n) => user.role === "superadmin" || n.roles.includes(user.role)).slice(0, 4);
  const creditsLeft = Math.max(0, (user.ai_credits || 0) - (user.ai_credits_used || 0));

  const NavList = ({ onNavigate }) => (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((item) => {
        const active = location.pathname === item.to || (item.to !== "/app" && location.pathname.startsWith(item.to));
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              active
                ? "bg-[hsl(var(--primary))] text-white shadow-lg shadow-[hsl(var(--primary))]/30"
                : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))] hover:text-[hsl(var(--foreground))]"
            }`}
          >
            <Icon className="w-5 h-5 shrink-0" strokeWidth={active ? 2.6 : 2.2} />
            <span className="flex-1">{item.label}</span>
            {item.to === "/app/multiplayer" && inviteCount > 0 && (
              <span data-testid="nav-invite-badge"
                className="ml-auto min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-[hsl(var(--primary))] text-white text-[11px] font-bold">
                {inviteCount}
              </span>
            )}
            {item.to === "/app/discover" && trailerNew > 0 && (
              <span data-testid="nav-trailer-badge" title={`${trailerNew} new trailer${trailerNew === 1 ? "" : "s"}`}
                className="ml-auto inline-flex items-center gap-0.5 min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[11px] font-bold shadow shadow-amber-500/30">
                🎬 {trailerNew}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 flex-col border-r border-[hsl(var(--border))] bg-[hsl(var(--card))]/60 backdrop-blur-xl">
        <div className="h-16 flex items-center px-5 border-b border-[hsl(var(--border))]">
          <Logo to="/app" size="md" />
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          <NavList />
        </div>
        <div className="p-3 border-t border-[hsl(var(--border))]">
          <Link to="/app/settings" data-testid="nav-settings"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]">
            <SettingsIcon className="w-5 h-5" /> Settings
          </Link>
        </div>
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-80 p-0 bg-[hsl(var(--card))]">
          <SheetTitle className="sr-only">Navigation menu</SheetTitle>
          <div className="h-20 flex items-center px-5 border-b border-[hsl(var(--border))]">
            <Logo to="/app" size="md" />
          </div>
          <div className="py-4"><NavList onNavigate={() => setMobileOpen(false)} /></div>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 flex items-center justify-between gap-3 px-4 lg:px-6 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 backdrop-blur-xl sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
            <div className="lg:hidden"><Logo to="/app" size="sm" showText={false} /></div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <GlobalSearch />
            <LivePlayersBadge />
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--secondary))] text-xs font-semibold" data-testid="ai-credits">
              <Zap className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> {creditsLeft} credits
            </div>
            <NotificationBell />
            <Button variant="outline" size="sm" onClick={toggle} className="rounded-full gap-1.5" data-testid="mode-toggle">
              {isKid ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              <span className="hidden sm:inline">{isKid ? "Studio" : "Kid"} Mode</span>
            </Button>
            <Link to="/app/profile" className="flex items-center gap-2 pl-1" data-testid="topbar-profile">
              <div className="w-9 h-9 rounded-full grid place-items-center text-lg bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] shadow-lg">
                {user.avatar_emoji || "🎮"}
              </div>
              <div className="hidden md:block leading-tight">
                <div className="text-sm font-bold">{user.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{user.role}</div>
              </div>
            </Link>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {platform.maintenance_mode && (
          <div data-testid="maintenance-banner"
            className="flex items-center gap-2 px-4 lg:px-6 py-2.5 text-sm font-semibold bg-amber-500/15 text-amber-600 border-b border-amber-500/20">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>Maintenance in progress — some features may be temporarily unavailable.</span>
          </div>
        )}
        {platform.announcement && !annDismissed && (
          <div data-testid="announcement-banner"
            className="flex items-center gap-2 px-4 lg:px-6 py-2.5 text-sm bg-[hsl(var(--primary))]/10 text-[hsl(var(--foreground))] border-b border-[hsl(var(--primary))]/20">
            <Megaphone className="w-4 h-4 shrink-0 text-[hsl(var(--primary))]" />
            <span className="flex-1">{platform.announcement}</span>
            <button onClick={dismissAnn} data-testid="dismiss-announcement"
              className="p-1 rounded-md hover:bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <main className="flex-1 overflow-y-auto pb-16 lg:pb-0">{children}</main>
      </div>

      {/* Mobile bottom tab bar — thumb-reachable primary nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]/95 backdrop-blur-xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
        <div className="flex items-stretch justify-around">
          {bottomItems.map((item) => {
            const active = location.pathname === item.to || (item.to !== "/app" && location.pathname.startsWith(item.to));
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} data-testid={`bottomnav-${item.label.toLowerCase()}`}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold transition-colors ${active ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))]"}`}>
                <Icon className="w-5 h-5" strokeWidth={active ? 2.6 : 2.2} />
                <span>{item.label}</span>
              </Link>
            );
          })}
          <button onClick={() => setMobileOpen(true)} data-testid="bottomnav-more"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold text-[hsl(var(--muted-foreground))]">
            <Menu className="w-5 h-5" strokeWidth={2.2} />
            <span>More</span>
          </button>
        </div>
      </nav>
      <LobbyDock />
      <DirectorAssistant />
      <InstallPrompt />
    </div>
  );
}
